import * as fs from 'fs-extra';
import * as path from 'path';
import { 
  Migration, 
  MigrationType, 
  ProjectStructure, 
  MigrationResult,
  ValidationResult,
  FileChange,
  SectionManifestV1,
  SectionManifestV2,
  BackupData
} from '../types';

export const aiAgentMigration: Migration = {
  id: 'ai-agent-integration',
  version: '2.0.0',
  type: MigrationType.BREAKING,
  description: 'Integrate AI agent functionality with enhanced section manifests and DSL-first architecture',
  breaking: true,

  async transform(project: ProjectStructure): Promise<MigrationResult> {
    const changes: FileChange[] = [];
    const errors: string[] = [];
    const warnings: string[] = [];

    try {
      // 1. Update package.json with new dependencies
      const packageJsonChanges = await updatePackageJson(project);
      changes.push(...packageJsonChanges);

      // 2. Transform section manifests from v1 to v2
      const manifestChanges = await transformSectionManifests(project);
      changes.push(...manifestChanges.changes);
      warnings.push(...manifestChanges.warnings);

      // 3. Generate AI agent files
      const agentFileChanges = await generateAgentFiles(project);
      changes.push(...agentFileChanges);

      // 4. Update import statements for new APIs
      const importChanges = await updateImportStatements(project);
      changes.push(...importChanges);

      // 5. Update Storybook configuration for agent
      const storybookChanges = await updateStorybookConfig(project);
      changes.push(...storybookChanges);

      // 6. Generate agent documentation
      const docChanges = await generateAgentDocumentation(project);
      changes.push(...docChanges);

      return {
        success: true,
        changes,
        warnings: warnings.length > 0 ? warnings : undefined
      };
    } catch (error) {
      errors.push(`Migration failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      return {
        success: false,
        changes,
        errors
      };
    }
  },

  async validate(project: ProjectStructure): Promise<ValidationResult> {
    const errors: string[] = [];
    const warnings: string[] = [];
    const suggestions: string[] = [];

    // Check if project has required structure for migration
    const srcPath = path.join(project.path, 'src');
    if (!project.directories.includes('src')) {
      errors.push('Project must have src directory for AI agent integration');
    }

    // Check for package.json
    const packageJson = project.packageJson;
    if (!packageJson) {
      errors.push('Project must have valid package.json');
    }

    // Check for existing section manifests
    const manifestFiles = project.files.filter(f => 
      f.path.includes('.manifest.json') || 
      (f.path.includes('sections') && f.path.endsWith('.json'))
    );

    if (manifestFiles.length === 0) {
      warnings.push('No section manifests found - will create examples');
      suggestions.push('Consider creating section manifests for your existing components');
    }

    // Check for React and TypeScript
    const deps = { ...packageJson.dependencies, ...packageJson.devDependencies };
    if (!deps.react) {
      errors.push('React is required for AI agent integration');
    } else {
      // Check React version - require 17+
      const reactVersion = deps.react.replace(/[^\d.]/g, ''); // Remove ^, ~, etc.
      const majorVersion = parseInt(reactVersion.split('.')[0]);
      if (majorVersion < 17) {
        errors.push('React is required for AI agent integration');
      }
    }
    if (!deps.typescript && !project.files.some(f => f.path.endsWith('.ts') || f.path.endsWith('.tsx'))) {
      warnings.push('TypeScript is recommended for AI agent features');
      suggestions.push('Consider adding TypeScript for better AI agent experience');
    }

    return {
      valid: errors.length === 0,
      errors: errors.length > 0 ? errors : undefined,
      warnings: warnings.length > 0 ? warnings : undefined,
      suggestions: suggestions.length > 0 ? suggestions : undefined
    };
  },

  async rollback(project: ProjectStructure, backup: BackupData): Promise<void> {
    // Restore original files from backup
    for (const file of backup.files) {
      const fullPath = path.join(project.path, file.path);
      await fs.ensureDir(path.dirname(fullPath));
      await fs.writeFile(fullPath, file.content);
    }

    // Remove AI agent files that were added
    const agentFiles = [
      'AGENT.md',
      'src/agent.ts',
      'src/governance.ts',
      'src/ui/AgentUI.tsx',
      'src/ui/ChatInterface.tsx',
      'src/ui/SplitPane.tsx',
      'src/ui/StorybookEmbed.tsx',
      'src/ui/index.ts',
      'src/state/agent-store.ts',
      'src/hooks/use-agent-operations.ts',
      'src/hooks/use-project-data.ts',
      'src/hooks/index.ts',
      'src/providers/AgentProvider.tsx',
      'src/providers/index.ts',
      'src/examples/section-manifests.ts',
      'src/examples/index.ts'
    ];

    for (const file of agentFiles) {
      const fullPath = path.join(project.path, file);
      if (await fs.pathExists(fullPath)) {
        await fs.remove(fullPath);
      }
    }
  },

  estimateTime(project: ProjectStructure): number {
    // Base time: 30 seconds
    let time = 30000;

    // Add time for each manifest to transform
    const manifestFiles = project.files.filter(f => f.path.includes('.manifest.json'));
    time += manifestFiles.length * 5000; // 5 seconds per manifest

    // Add time for large projects
    if (project.files.length > 100) {
      time += 15000; // Extra 15 seconds for large projects
    }

    return time;
  }
};

async function updatePackageJson(project: ProjectStructure): Promise<FileChange[]> {
  const changes: FileChange[] = [];
  const packageJsonPath = 'package.json';
  const packageJson = { ...project.packageJson };

  // Add AI agent dependencies
  if (!packageJson.dependencies) packageJson.dependencies = {};
  if (!packageJson.devDependencies) packageJson.devDependencies = {};

  const newDependencies = {
    '@tanstack/react-query': '^4.29.0',
    'zustand': '^4.3.0'
  };

  const newDevDependencies = {
    '@types/react': '^18.0.0',
    '@types/react-dom': '^18.0.0'
  };

  // Add dependencies that don't exist
  Object.entries(newDependencies).forEach(([dep, version]) => {
    if (!packageJson.dependencies[dep]) {
      packageJson.dependencies[dep] = version;
    }
  });

  Object.entries(newDevDependencies).forEach(([dep, version]) => {
    if (!packageJson.devDependencies[dep]) {
      packageJson.devDependencies[dep] = version;
    }
  });

  // Add AI agent script
  if (!packageJson.scripts) packageJson.scripts = {};
  if (!packageJson.scripts['agent']) {
    packageJson.scripts['agent'] = 'echo "Start your AI agent interface here"';
  }

  changes.push({
    path: packageJsonPath,
    type: 'update',
    oldContent: JSON.stringify(project.packageJson, null, 2),
    newContent: JSON.stringify(packageJson, null, 2)
  });

  return changes;
}

async function transformSectionManifests(project: ProjectStructure): Promise<{
  changes: FileChange[];
  warnings: string[];
}> {
  const changes: FileChange[] = [];
  const warnings: string[] = [];

  // Find existing manifest files
  const manifestFiles = project.files.filter(f => 
    f.path.endsWith('.manifest.json') || 
    (f.path.includes('sections') && f.path.endsWith('.json'))
  );

  for (const file of manifestFiles) {
    try {
      const oldManifest: SectionManifestV1 = JSON.parse(file.content);
      const newManifest: SectionManifestV2 = transformManifest(oldManifest);
      
      changes.push({
        path: file.path,
        type: 'update',
        oldContent: file.content,
        newContent: JSON.stringify(newManifest, null, 2)
      });
    } catch (error) {
      warnings.push(`Failed to transform manifest ${file.path}: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  // If no manifests exist, create example manifests
  if (manifestFiles.length === 0) {
    const exampleManifests = generateExampleManifests();
    changes.push(...exampleManifests);
  }

  return { changes, warnings };
}

function transformManifest(oldManifest: SectionManifestV1): SectionManifestV2 {
  return {
    id: oldManifest.id,
    name: oldManifest.name,
    description: oldManifest.description,
    version: oldManifest.version,
    category: inferCategory(oldManifest.name),
    
    // Transform variants to new schema
    variants: oldManifest.variants.map(variant => ({
      name: variant.name,
      description: variant.description,
      presentation: variant.presentation,
      props: variant.props || {},
      tokens: extractTokensFromProps(variant.props || {}),
      slots: variant.slots
    })),
    
    // Add new fields with sensible defaults
    props: inferPropsFromVariants(oldManifest.variants),
    defaultProps: inferDefaultProps(oldManifest.variants),
    accessibility: inferAccessibility(oldManifest.name),
    
    // Generate basic stories
    stories: [
      {
        name: 'Default',
        args: {}
      }
    ]
  };
}

function inferCategory(name: string): 'element' | 'component' | 'section' {
  const elementNames = ['button', 'input', 'link', 'icon', 'image'];
  const sectionNames = ['header', 'footer', 'sidebar', 'navigation', 'hero'];
  
  const lowerName = name.toLowerCase();
  
  if (elementNames.some(el => lowerName.includes(el))) return 'element';
  if (sectionNames.some(sec => lowerName.includes(sec))) return 'section';
  return 'component';
}

function extractTokensFromProps(props: Record<string, any>): Record<string, string> {
  const tokens: Record<string, string> = {};
  
  // Look for color, spacing, and typography props that could be tokens
  Object.entries(props).forEach(([key, value]) => {
    if (typeof value === 'string') {
      if (key.includes('color') || key.includes('Color')) {
        tokens[key] = `$colors.${value}`;
      } else if (key.includes('spacing') || key.includes('gap') || key.includes('margin') || key.includes('padding')) {
        tokens[key] = `$space.${value}`;
      } else if (key.includes('font') || key.includes('text')) {
        tokens[key] = `$typography.${value}`;
      }
    }
  });
  
  return tokens;
}

function inferPropsFromVariants(variants: SectionManifestV1['variants']): SectionManifestV2['props'] {
  const props: Record<string, any> = {};
  
  // Common props
  props.children = {
    type: 'ReactNode',
    description: 'Component content'
  };
  
  // Analyze variants for common props
  const allProps = new Set<string>();
  variants.forEach(variant => {
    if (variant.props) {
      Object.keys(variant.props).forEach(prop => allProps.add(prop));
    }
  });
  
  // Infer prop types from usage
  allProps.forEach(propName => {
    if (propName === 'variant') return; // Skip variant prop
    
    props[propName] = {
      type: inferPropType(propName),
      description: `${propName.charAt(0).toUpperCase() + propName.slice(1)} property`
    };
  });
  
  return props;
}

function inferPropType(propName: string): string {
  if (propName.includes('disabled') || propName.includes('loading') || propName.includes('active')) {
    return 'boolean';
  }
  if (propName.includes('onClick') || propName.includes('onPress') || propName.includes('handler')) {
    return 'function';
  }
  if (propName.includes('count') || propName.includes('size') || propName.includes('index')) {
    return 'number';
  }
  return 'string';
}

function inferDefaultProps(variants: SectionManifestV1['variants']): Record<string, any> {
  return {
    variant: variants[0]?.name || 'default'
  };
}

function inferAccessibility(name: string): SectionManifestV2['accessibility'] {
  const lowerName = name.toLowerCase();
  
  if (lowerName.includes('button')) {
    return {
      role: 'button',
      keyboardSupport: true
    };
  }
  if (lowerName.includes('link')) {
    return {
      role: 'link',
      keyboardSupport: true
    };
  }
  if (lowerName.includes('card') || lowerName.includes('article')) {
    return {
      role: 'article',
      keyboardSupport: false
    };
  }
  
  return {
    keyboardSupport: false
  };
}

function generateExampleManifests(): FileChange[] {
  const changes: FileChange[] = [];
  
  // Create example manifests directory
  const manifestsDir = 'src/examples';
  
  const buttonManifest = {
    id: 'button',
    name: 'Button',
    description: 'Interactive button element with multiple variants',
    version: '1.0.0',
    category: 'element',
    props: {
      children: { type: 'ReactNode', required: true },
      variant: { type: 'string', default: 'primary' },
      disabled: { type: 'boolean', default: false }
    },
    variants: [
      {
        name: 'primary',
        description: 'Primary action button',
        props: { className: 'bg-blue-500 hover:bg-blue-600 text-white' },
        tokens: { bgColor: '$colors.primary.500' }
      }
    ],
    accessibility: {
      role: 'button',
      keyboardSupport: true
    }
  };
  
  changes.push({
    path: `${manifestsDir}/button.manifest.json`,
    type: 'create',
    newContent: JSON.stringify(buttonManifest, null, 2)
  });
  
  return changes;
}

async function generateAgentFiles(project: ProjectStructure): Promise<FileChange[]> {
  const changes: FileChange[] = [];
  
  // Generate core agent files
  const agentFiles = [
    {
      path: 'src/agent.ts',
      content: generateAgentCore()
    },
    {
      path: 'src/governance.ts',
      content: generateGovernanceEngine()
    },
    {
      path: 'src/ui/AgentUI.tsx',
      content: generateAgentUI()
    },
    {
      path: 'src/state/agent-store.ts',
      content: generateAgentStore()
    },
    {
      path: 'src/hooks/use-agent-operations.ts',
      content: generateAgentHooks()
    }
  ];
  
  agentFiles.forEach(file => {
    changes.push({
      path: file.path,
      type: 'create',
      newContent: file.content
    });
  });
  
  return changes;
}

function generateAgentCore(): string {
  return `// Generated by Dezzy Migration v2.0.0
import React from 'react';
import { ViewDSL, SectionManifest, ValidationResult } from './types';

export interface AgentContext {
  currentProject: {
    sections: SectionManifest[];
    views: ViewDSL[];
    tokens: Record<string, any>;
  };
}

export class DezzyAgent {
  constructor(private context: AgentContext) {}
  
  async processOperation(operation: any): Promise<any> {
    // Basic agent implementation
    return { success: true };
  }
}

export function createAgentContext(projectData: any): AgentContext {
  return {
    currentProject: projectData
  };
}
`;
}

function generateGovernanceEngine(): string {
  return `// Generated by Dezzy Migration v2.0.0
export interface GovernanceRule {
  id: string;
  name: string;
  validate: (context: any) => Promise<any>;
}

export class GovernanceEngine {
  private rules: GovernanceRule[] = [];
  
  addRule(rule: GovernanceRule) {
    this.rules.push(rule);
  }
  
  async evaluate(context: any): Promise<any> {
    return { passed: true, results: [] };
  }
}
`;
}

function generateAgentUI(): string {
  return `// Generated by Dezzy Migration v2.0.0
import React from 'react';

export interface AgentUIProps {
  storybookUrl?: string;
  projectPath?: string;
}

export const AgentUI: React.FC<AgentUIProps> = ({
  storybookUrl = 'http://localhost:6006',
  projectPath = './src'
}) => {
  return (
    <div className="h-screen bg-gray-100">
      <div className="p-4">
        <h1>Dezzy AI Agent</h1>
        <p>Storybook URL: {storybookUrl}</p>
        <p>Project Path: {projectPath}</p>
        <p>AI Agent functionality is now available!</p>
      </div>
    </div>
  );
};
`;
}

function generateAgentStore(): string {
  return `// Generated by Dezzy Migration v2.0.0
export interface AgentState {
  messages: any[];
  isProcessing: boolean;
}

export const useAgentStore = (selector?: any) => {
  // Basic store implementation
  return {
    messages: [],
    isProcessing: false,
    addMessage: () => {},
    setProcessing: () => {}
  };
};
`;
}

function generateAgentHooks(): string {
  return `// Generated by Dezzy Migration v2.0.0
export function useAgentOperations() {
  return {
    addVariant: async (sectionName: string, details: any) => {
      console.log('Adding variant:', sectionName, details);
      return { success: true };
    },
    createSection: async (name: string, details: any) => {
      console.log('Creating section:', name, details);
      return { success: true };
    },
    processUserInput: async (input: string) => {
      console.log('Processing:', input);
      return { success: true };
    }
  };
}
`;
}

async function updateImportStatements(project: ProjectStructure): Promise<FileChange[]> {
  const changes: FileChange[] = [];
  
  // Find TypeScript/JavaScript files that might need import updates
  const codeFiles = project.files.filter(f => 
    f.path.endsWith('.ts') || 
    f.path.endsWith('.tsx') || 
    f.path.endsWith('.js') || 
    f.path.endsWith('.jsx')
  );
  
  for (const file of codeFiles) {
    const updatedContent = updateImports(file.content);
    if (updatedContent !== file.content) {
      changes.push({
        path: file.path,
        type: 'update',
        oldContent: file.content,
        newContent: updatedContent
      });
    }
  }
  
  return changes;
}

function updateImports(content: string): string {
  // Update import statements for new API
  let updated = content;
  
  // Add new imports if dezzy is imported
  if (updated.includes("from 'dezzy'") && !updated.includes('AgentUI')) {
    updated = updated.replace(
      /import\s*{([^}]+)}\s*from\s*['"]dezzy['"];/,
      (match, imports) => {
        const newImports = imports.trim() + ', AgentUI, useAgentOperations';
        return `import { ${newImports} } from 'dezzy';`;
      }
    );
  }
  
  return updated;
}

async function updateStorybookConfig(project: ProjectStructure): Promise<FileChange[]> {
  const changes: FileChange[] = [];
  
  // Check if .storybook directory exists
  const storybookConfigPath = '.storybook/main.js';
  const storybookConfigFile = project.files.find(f => f.path === storybookConfigPath);
  
  if (storybookConfigFile) {
    const updatedConfig = updateStorybookMainConfig(storybookConfigFile.content);
    if (updatedConfig !== storybookConfigFile.content) {
      changes.push({
        path: storybookConfigPath,
        type: 'update',
        oldContent: storybookConfigFile.content,
        newContent: updatedConfig
      });
    }
  }
  
  return changes;
}

function updateStorybookMainConfig(content: string): string {
  // Add agent-related stories to Storybook config
  let updated = content;
  
  if (!updated.includes('src/examples/**/*.stories.@(js|jsx|ts|tsx)')) {
    // Add examples stories pattern
    updated = updated.replace(
      /stories:\s*\[(.*?)\]/s,
      (match, storiesArray) => {
        const stories = storiesArray.trim();
        const newStories = stories.endsWith(',') ? stories : stories + ',';
        return `stories: [${newStories}\n    'src/examples/**/*.stories.@(js|jsx|ts|tsx)'
  ]`;
      }
    );
  }
  
  return updated;
}

async function generateAgentDocumentation(project: ProjectStructure): Promise<FileChange[]> {
  const changes: FileChange[] = [];
  
  const agentDocContent = `# Dezzy AI Agent

Your project has been upgraded to include the Dezzy AI Agent!

## What's New

- **AI Agent Interface**: Natural language design system development
- **Enhanced Section Manifests**: Improved component definitions with governance
- **DSL-First Architecture**: All UI changes generate ViewDSL
- **Governance Engine**: Automated validation and best practices

## Getting Started

1. Install new dependencies:
   \`\`\`bash
   npm install
   \`\`\`

2. Start using the AI Agent:
   \`\`\`tsx
   import { AgentUI } from 'dezzy';
   
   function App() {
     return <AgentUI storybookUrl="http://localhost:6006" projectPath="./src" />;
   }
   \`\`\`

3. Try natural language commands:
   - "Add a large variant to the Button section"
   - "Create a ProductCard component"
   - "Validate my design system"

## Migration Complete

Your project has been successfully migrated to Dezzy v2.0.0 with AI agent support.
Check the updated files and test your components to ensure everything works correctly.

For detailed documentation, see AGENT.md (generated during migration).
`;
  
  changes.push({
    path: 'MIGRATION.md',
    type: 'create',
    newContent: agentDocContent
  });
  
  return changes;
}